export const LIVE_MARKETPLACE_SOURCE = "supabase";

const DB_ROLE_TO_APP_ROLE = Object.freeze({
  member: "parishioner",
  provider: "free_provider",
  enhanced_provider: "paid_provider",
  admin: "admin",
  owner: "owner",
});

function compactText(value) {
  return String(value || "").trim();
}

function compactArray(value) {
  return Array.isArray(value)
    ? value.map((item) => compactText(item)).filter(Boolean)
    : [];
}

function cleanObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function toAppRole(databaseRole) {
  return DB_ROLE_TO_APP_ROLE[compactText(databaseRole)] || "visitor";
}

export function initialsFromName(name) {
  const parts = compactText(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  return (parts.map((part) => part[0]).join("") || "CN").toUpperCase();
}

export function toAppUser({ user, providerProfile = null, emailVerified, phoneVerified, parishVerified } = {}) {
  if (!user) return null;

  const publicFields = cleanObject(user.public_profile_fields);
  const privateFields = cleanObject(user.private_contact_fields);
  const fullName = compactText(user.full_name) || compactText(user.email) || "Coptic Neighbor member";
  const role = toAppRole(user.role);

  return {
    id: user.id,
    database_id: user.id,
    provider_profile_id: providerProfile?.id || null,
    full_name: fullName,
    email: compactText(user.email || privateFields.email),
    phone: compactText(user.phone || privateFields.phone),
    parish_id: compactText(publicFields.parish_id),
    city: compactText(user.city || publicFields.city),
    state: compactText(user.state || publicFields.state),
    role,
    bio: compactText(providerProfile?.bio || publicFields.bio),
    languages: compactArray(publicFields.languages).length ? compactArray(publicFields.languages) : ["English"],
    is_email_verified: Boolean(emailVerified ?? user.status === "active"),
    is_phone_verified: Boolean(phoneVerified),
    parish_verified: Boolean(parishVerified),
    license_verified: false,
    avatar_seed: compactText(publicFields.avatar_seed) || initialsFromName(fullName),
    source: LIVE_MARKETPLACE_SOURCE,
  };
}

export function toAppListing({ listing, providerUser, providerProfile = null } = {}) {
  if (!listing) return null;

  const providerFields = cleanObject(providerUser?.public_profile_fields);
  const providerUserId = providerUser?.id || listing.provider_user_id || listing.provider_id;
  const slug = compactText(listing.slug) || compactText(listing.id);

  return {
    id: slug,
    database_id: listing.id,
    provider_profile_id: providerProfile?.id || listing.provider_profile_id || listing.provider_id,
    provider_id: providerUserId,
    category_id: listing.category_id,
    parish_id: compactText(providerFields.parish_id || listing.parish_id),
    slug,
    title: compactText(listing.title),
    description: compactText(listing.description),
    price_type: compactText(listing.price_type) || "negotiable",
    price_amount: numberOrNull(listing.price_amount),
    is_remote: Boolean(listing.is_remote),
    is_in_person: Boolean(listing.is_in_person),
    service_area: compactText(listing.service_area),
    city: compactText(listing.city || providerUser?.city),
    state: compactText(listing.state || providerUser?.state),
    languages: compactArray(listing.languages).length ? compactArray(listing.languages) : ["English"],
    availability: compactText(listing.availability),
    contact_pref: compactText(providerProfile?.public_contact_preference || listing.public_contact_preference) || "platform_only",
    photos: Number.isFinite(Number(listing.photos)) ? Math.max(0, Number(listing.photos)) : 0,
    tags: compactArray(listing.tags),
    status: compactText(listing.status),
    moderation_status: compactText(listing.moderation_status),
    rating: numberOrNull(listing.rating) || 0,
    review_count: numberOrNull(listing.review_count) || 0,
    view_count: numberOrNull(listing.view_count) || 0,
    is_featured: Boolean(listing.featured ?? listing.is_featured),
    published_at: listing.published_at || null,
    created_at: listing.created_at || null,
    updated_at: listing.updated_at || null,
    source: LIVE_MARKETPLACE_SOURCE,
  };
}

export function toPublicListing(row = {}) {
  const provider = {
    id: row.provider_user_id,
    full_name: compactText(row.provider_full_name),
    city: compactText(row.provider_city),
    state: compactText(row.provider_state),
    role: "provider",
    status: "active",
    public_profile_fields: {
      ...cleanObject(row.provider_public_profile_fields),
      bio: compactText(row.provider_bio || cleanObject(row.provider_public_profile_fields).bio),
    },
  };
  const providerProfile = {
    id: row.provider_profile_id,
    user_id: row.provider_user_id,
    bio: compactText(row.provider_bio),
    public_contact_preference: compactText(row.public_contact_preference) || "platform_only",
  };

  return {
    provider: toAppUser({ user: provider, providerProfile, emailVerified: true }),
    listing: toAppListing({
      listing: {
        ...row,
        provider_id: row.provider_profile_id,
        provider_user_id: row.provider_user_id,
      },
      providerUser: provider,
      providerProfile,
    }),
  };
}

export function buildLiveListingInsert({ providerProfileId, provider, pendingListing } = {}) {
  return {
    provider_id: providerProfileId,
    category_id: pendingListing.category_id,
    slug: pendingListing.slug,
    title: pendingListing.title,
    description: pendingListing.description,
    price_type: pendingListing.price_type || "negotiable",
    price_amount: numberOrNull(pendingListing.price_amount),
    service_area: pendingListing.service_area,
    city: compactText(provider?.city),
    state: compactText(provider?.state),
    is_remote: Boolean(pendingListing.is_remote),
    is_in_person: Boolean(pendingListing.is_in_person),
    availability: compactText(pendingListing.availability),
    languages: compactArray(pendingListing.languages).length ? compactArray(pendingListing.languages) : ["English"],
    tags: compactArray(pendingListing.tags),
    status: "pending_review",
    moderation_status: "pending",
    featured: false,
  };
}

export function toProfileUpsert({ authUser, draft, fallbackRole = "member" } = {}) {
  const publicDraft = cleanObject(draft?.public);
  const privateDraft = cleanObject(draft?.private);
  const role = fallbackRole === "provider" || fallbackRole === "member" ? fallbackRole : "member";
  const email = compactText(privateDraft.email || authUser?.email);

  return {
    id: authUser?.id,
    email,
    phone: compactText(privateDraft.phone),
    full_name: compactText(publicDraft.full_name || authUser?.user_metadata?.full_name || email),
    city: compactText(publicDraft.city),
    state: compactText(publicDraft.state).toUpperCase().slice(0, 2),
    preferred_language: "en",
    role,
    status: authUser?.email_confirmed_at ? "active" : "pending_email",
    public_profile_fields: {
      parish_id: compactText(publicDraft.parish_id),
      languages: compactArray(publicDraft.languages).length ? compactArray(publicDraft.languages) : ["English"],
      bio: compactText(publicDraft.bio),
      avatar_seed: initialsFromName(publicDraft.full_name || authUser?.email),
    },
    private_contact_fields: {
      email,
      phone: compactText(privateDraft.phone),
    },
  };
}
